import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmt, CURRENCIES, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Wallet, Stethoscope, Trash2, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";
import { InvoiceDialog } from "@/pages/Invoices";

const EMPTY_RATES = { TRY: "1", USD: "", EUR: "", GBP: "" };

export default function PatientDetail() {
    const { id } = useParams();
    const [p, setP] = useState(null);
    const [tOpen, setTOpen] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const [tForm, setTForm] = useState({
        title: "",
        description: "",
        total_amount: "",
        currency: "TRY",
        rates: { ...EMPTY_RATES },
        down_payment: "0",
        installments: 1,
    });
    const [payForm, setPayForm] = useState({
        treatment_id: "",
        amount: "",
        currency: "TRY",
        method: "cash",
        note: "",
    });
    const [liveRates, setLiveRates] = useState(null);
    const [loadingRates, setLoadingRates] = useState(false);
    const [invoice, setInvoice] = useState(null);

    async function fetchLiveRates() {
        setLoadingRates(true);
        try {
            const { data } = await api.get("/exchange-rates");
            setLiveRates(data);
            setTForm((f) => ({
                ...f,
                rates: {
                    TRY: "1",
                    USD: String(data.USD || ""),
                    EUR: String(data.EUR || ""),
                    GBP: String(data.GBP || ""),
                },
            }));
            toast.success("Bugünkü kurlar yüklendi");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoadingRates(false);
        }
    }

    async function load() {
        const { data } = await api.get(`/patients/${id}`);
        setP(data);
    }
    useEffect(() => {
        load();
    }, [id]);

    async function submitTreatment(e) {
        e.preventDefault();
        // Build rates map — only include non-empty entries; TRY defaults to 1
        const rates = {};
        for (const k of Object.keys(tForm.rates)) {
            const v = Number(tForm.rates[k]);
            if (!isNaN(v) && v > 0) rates[k] = v;
        }
        rates.TRY = 1.0;
        // Guarantee treatment currency has a rate
        if (!rates[tForm.currency] && tForm.currency !== "TRY") {
            toast.error(
                `Anlaşma para birimi ${tForm.currency} için TL kuru girmelisiniz`,
            );
            return;
        }
        try {
            await api.post("/treatments", {
                patient_id: id,
                title: tForm.title,
                description: tForm.description,
                total_amount: Number(tForm.total_amount),
                currency: tForm.currency,
                exchange_rate_try: rates[tForm.currency] || 1,
                rates,
                down_payment: Number(tForm.down_payment),
                installments: Number(tForm.installments),
            });
            toast.success("Tedavi anlaşması oluşturuldu");
            setTOpen(false);
            setTForm({
                title: "",
                description: "",
                total_amount: "",
                currency: "TRY",
                rates: { ...EMPTY_RATES },
                down_payment: "0",
                installments: 1,
            });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function submitPayment(e) {
        e.preventDefault();
        try {
            await api.post("/payments", {
                ...payForm,
                amount: Number(payForm.amount),
            });
            toast.success("Ödeme kaydedildi");
            setPayOpen(false);
            setPayForm({
                treatment_id: "",
                amount: "",
                currency: "TRY",
                method: "cash",
                note: "",
            });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function deletePayment(payId) {
        if (!window.confirm("Bu ödeme kaydını silmek istediğinize emin misiniz?"))
            return;
        try {
            await api.delete(`/payments/${payId}`);
            toast.success("Ödeme silindi");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function makeInvoice(pay) {
        if (!window.confirm("Bu ödeme için e-fatura kesilsin mi? (1 fatura kontürü düşer)")) return;
        try {
            const { data } = await api.post("/invoices", { payment_id: pay.id });
            toast.success(`Fatura oluşturuldu: ${data.number}`);
            setInvoice(data);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function showInvoice(id) {
        const { data } = await api.get(`/invoices/${id}`);
        setInvoice(data);
    }

    if (!p) return <div>Yükleniyor...</div>;

    return (
        <div data-testid="patient-detail-page">
            <Link
                to="/dashboard/patients"
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            >
                <ArrowLeft className="w-4 h-4" /> Hastalar
            </Link>
            <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">
                        {p.name}
                    </h1>
                    <p className="text-slate-600 mt-1">
                        {p.phone || "Telefon yok"} •{" "}
                        {p.id_no || "TC/Pasaport yok"}
                    </p>
                    {p.notes && (
                        <p className="mt-2 text-sm text-slate-500">{p.notes}</p>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                    <Dialog open={tOpen} onOpenChange={setTOpen}>
                        <DialogTrigger asChild>
                            <Button
                                variant="outline"
                                data-testid="add-treatment-btn"
                            >
                                <Stethoscope className="w-4 h-4 mr-1" /> Yeni
                                Tedavi
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>
                                    Yeni Tedavi Anlaşması
                                </DialogTitle>
                            </DialogHeader>
                            <form
                                onSubmit={submitTreatment}
                                className="space-y-3"
                            >
                                <div>
                                    <Label>Başlık</Label>
                                    <Input
                                        required
                                        value={tForm.title}
                                        onChange={(e) =>
                                            setTForm({
                                                ...tForm,
                                                title: e.target.value,
                                            })
                                        }
                                        placeholder="İmplant, kanal tedavisi, ortodonti..."
                                        data-testid="treatment-title-input"
                                    />
                                </div>
                                <div>
                                    <Label>Açıklama</Label>
                                    <Textarea
                                        value={tForm.description}
                                        onChange={(e) =>
                                            setTForm({
                                                ...tForm,
                                                description: e.target.value,
                                            })
                                        }
                                        data-testid="treatment-desc-input"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Toplam Tutar</Label>
                                        <Input
                                            required
                                            type="number"
                                            step="0.01"
                                            value={tForm.total_amount}
                                            onChange={(e) =>
                                                setTForm({
                                                    ...tForm,
                                                    total_amount:
                                                        e.target.value,
                                                })
                                            }
                                            data-testid="treatment-total-input"
                                        />
                                    </div>
                                    <div>
                                        <Label>Anlaşma Para Birimi</Label>
                                        <Select
                                            value={tForm.currency}
                                            onValueChange={(v) =>
                                                setTForm({
                                                    ...tForm,
                                                    currency: v,
                                                })
                                            }
                                        >
                                            <SelectTrigger data-testid="treatment-currency-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CURRENCIES.map((c) => (
                                                    <SelectItem
                                                        key={c.code}
                                                        value={c.code}
                                                    >
                                                        {c.symbol} {c.code} -{" "}
                                                        {c.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="border rounded-lg p-3 bg-teal-50/40">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-teal-800">
                                            Anlaşma Anındaki TL Kurları
                                        </div>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={fetchLiveRates}
                                            disabled={loadingRates}
                                            className="h-7 text-xs"
                                            data-testid="load-live-rates-btn"
                                        >
                                            <RefreshCw
                                                className={`w-3.5 h-3.5 mr-1 ${loadingRates ? "animate-spin" : ""}`}
                                            />
                                            Bugünkü Kurları Yükle
                                        </Button>
                                    </div>
                                    <p className="text-xs text-slate-500 mb-3">
                                        Hastanın farklı para biriminde
                                        yapacağı ödemeler bu sabit kurlarla
                                        anlaşma dövizine çevrilerek bakiyeye
                                        işlenir.
                                        {liveRates?.updated_at && (
                                            <span className="block text-teal-700 mt-1">
                                                Canlı kur güncelleme:{" "}
                                                {liveRates.updated_at}
                                            </span>
                                        )}
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {CURRENCIES.map((c) => (
                                            <div key={c.code}>
                                                <Label className="text-xs">
                                                    1 {c.code} = ? TL
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.0001"
                                                    disabled={c.code === "TRY"}
                                                    value={
                                                        c.code === "TRY"
                                                            ? "1"
                                                            : tForm.rates[
                                                                  c.code
                                                              ]
                                                    }
                                                    onChange={(e) =>
                                                        setTForm({
                                                            ...tForm,
                                                            rates: {
                                                                ...tForm.rates,
                                                                [c.code]:
                                                                    e.target
                                                                        .value,
                                                            },
                                                        })
                                                    }
                                                    placeholder="0.00"
                                                    data-testid={`treatment-rate-${c.code}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Ön Ödeme</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={tForm.down_payment}
                                            onChange={(e) =>
                                                setTForm({
                                                    ...tForm,
                                                    down_payment:
                                                        e.target.value,
                                                })
                                            }
                                            data-testid="treatment-down-input"
                                        />
                                    </div>
                                    <div>
                                        <Label>Taksit Sayısı</Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={tForm.installments}
                                            onChange={(e) =>
                                                setTForm({
                                                    ...tForm,
                                                    installments: e.target
                                                        .value,
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                                <Button
                                    type="submit"
                                    className="dk-btn-primary w-full"
                                    data-testid="treatment-submit-btn"
                                >
                                    Anlaşmayı Kaydet
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={payOpen} onOpenChange={setPayOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="dk-btn-primary"
                                data-testid="add-payment-btn"
                                disabled={p.treatments.length === 0}
                            >
                                <Wallet className="w-4 h-4 mr-1" /> Ödeme Al
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Ödeme Kaydı</DialogTitle>
                            </DialogHeader>
                            <form
                                onSubmit={submitPayment}
                                className="space-y-3"
                            >
                                <div>
                                    <Label>Tedavi</Label>
                                    <Select
                                        value={payForm.treatment_id}
                                        onValueChange={(v) => {
                                            const t = p.treatments.find(
                                                (x) => x.id === v,
                                            );
                                            setPayForm({
                                                ...payForm,
                                                treatment_id: v,
                                                currency:
                                                    t?.currency ||
                                                    payForm.currency,
                                            });
                                        }}
                                    >
                                        <SelectTrigger data-testid="payment-treatment-select">
                                            <SelectValue placeholder="Tedavi seçin" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {p.treatments.map((t) => (
                                                <SelectItem
                                                    key={t.id}
                                                    value={t.id}
                                                >
                                                    {t.title} —{" "}
                                                    {fmt(
                                                        t.remaining,
                                                        t.currency,
                                                    )}{" "}
                                                    kaldı
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Tutar</Label>
                                        <Input
                                            required
                                            type="number"
                                            step="0.01"
                                            value={payForm.amount}
                                            onChange={(e) =>
                                                setPayForm({
                                                    ...payForm,
                                                    amount: e.target.value,
                                                })
                                            }
                                            data-testid="payment-amount-input"
                                        />
                                    </div>
                                    <div>
                                        <Label>Para Birimi</Label>
                                        <Select
                                            value={payForm.currency}
                                            onValueChange={(v) =>
                                                setPayForm({
                                                    ...payForm,
                                                    currency: v,
                                                })
                                            }
                                        >
                                            <SelectTrigger data-testid="payment-currency-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CURRENCIES.map((c) => (
                                                    <SelectItem
                                                        key={c.code}
                                                        value={c.code}
                                                    >
                                                        {c.symbol} {c.code}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label>Ödeme Yöntemi</Label>
                                    <Select
                                        value={payForm.method}
                                        onValueChange={(v) =>
                                            setPayForm({
                                                ...payForm,
                                                method: v,
                                            })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="cash">
                                                Nakit
                                            </SelectItem>
                                            <SelectItem value="card">
                                                Kredi Kartı
                                            </SelectItem>
                                            <SelectItem value="transfer">
                                                Havale
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Not</Label>
                                    <Input
                                        value={payForm.note}
                                        onChange={(e) =>
                                            setPayForm({
                                                ...payForm,
                                                note: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="dk-btn-primary w-full"
                                    data-testid="payment-submit-btn"
                                >
                                    Ödemeyi Kaydet
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="mt-8 space-y-4">
                {p.treatments.length === 0 && (
                    <Card>
                        <CardContent className="p-12 text-center text-slate-500">
                            Bu hasta için henüz tedavi anlaşması yok.
                        </CardContent>
                    </Card>
                )}
                {p.treatments.map((t) => (
                    <Card key={t.id}>
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-xl font-semibold">
                                        {t.title}
                                    </h3>
                                    {t.description && (
                                        <p className="text-sm text-slate-600 mt-1">
                                            {t.description}
                                        </p>
                                    )}
                                </div>
                                <div className="text-right">
                                    <div className="text-xs uppercase tracking-wide text-slate-500">
                                        Toplam
                                    </div>
                                    <div className="text-2xl font-bold mono">
                                        {fmt(t.total_amount, t.currency)}
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-4">
                                <div className="bg-emerald-50 rounded-lg p-3">
                                    <div className="text-xs text-emerald-700 font-medium">
                                        Ödenen ({t.currency} karşılığı)
                                    </div>
                                    <div className="text-lg font-semibold mono text-emerald-800">
                                        {fmt(t.paid_amount, t.currency)}
                                    </div>
                                </div>
                                <div className="bg-amber-50 rounded-lg p-3">
                                    <div className="text-xs text-amber-700 font-medium">
                                        Kalan Bakiye
                                    </div>
                                    <div className="text-lg font-semibold mono text-amber-800">
                                        {fmt(t.remaining, t.currency)}
                                    </div>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3">
                                    <div className="text-xs text-slate-600 font-medium">
                                        Taksit
                                    </div>
                                    <div className="text-lg font-semibold">
                                        {t.installments}
                                    </div>
                                </div>
                            </div>
                            {t.payments.length > 0 && (
                                <div className="mt-4 border-t pt-4">
                                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                                        Ödeme Geçmişi
                                    </div>
                                    <div className="space-y-1.5">
                                        {t.payments.map((pay) => (
                                            <div
                                                key={pay.id}
                                                className="flex items-center justify-between text-sm py-1.5 border-b last:border-b-0 gap-3"
                                            >
                                                <span className="text-slate-500 truncate">
                                                    {pay.created_at?.slice(
                                                        0,
                                                        10,
                                                    )}{" "}
                                                    •{" "}
                                                    {pay.method === "cash"
                                                        ? "Nakit"
                                                        : pay.method ===
                                                            "card"
                                                          ? "Kredi Kartı"
                                                          : "Havale"}
                                                    {pay.note &&
                                                        ` • ${pay.note}`}
                                                </span>
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <div className="text-right">
                                                        <div className="font-semibold mono text-emerald-700">
                                                            +
                                                            {fmt(
                                                                pay.amount,
                                                                pay.currency,
                                                            )}
                                                        </div>
                                                        {pay.currency !==
                                                            t.currency && (
                                                            <div className="text-[11px] text-slate-500 mono">
                                                                ≈{" "}
                                                                {fmt(
                                                                    pay.converted_amount,
                                                                    t.currency,
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className={`h-8 px-2 text-xs ${pay.invoice_id ? "text-emerald-700 border-emerald-200" : "text-amber-700 border-amber-200 hover:bg-amber-50"}`}
                                                        onClick={() =>
                                                            pay.invoice_id
                                                                ? showInvoice(pay.invoice_id)
                                                                : makeInvoice(pay)
                                                        }
                                                        data-testid={`payment-invoice-${pay.id}`}
                                                        title={pay.invoice_id ? "Faturayı görüntüle" : "E-fatura kes"}
                                                    >
                                                        <FileText className="w-3.5 h-3.5 mr-1" />
                                                        {pay.invoice_id ? "Fatura" : "Fatura Kes"}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-slate-400 hover:text-rose-600 h-8 w-8 p-0"
                                                        onClick={() =>
                                                            deletePayment(
                                                                pay.id,
                                                            )
                                                        }
                                                        data-testid={`payment-delete-${pay.id}`}
                                                        title="Ödemeyi sil"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>
            <InvoiceDialog inv={invoice} onClose={() => setInvoice(null)} />
        </div>
    );
}
